import { AxiosError } from 'axios';
import { useRef, useState } from 'react';
import { Control, useFieldArray, UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useClickAway } from 'react-use';
import { ErrorInfo, TreeCompany } from '../../../../api/model';
import { usePostOrganizationsCheck } from '../../../../api/origanization/origanization';
import { useDefaultOptions } from '../../../../hooks/api';
import OkModal from '../../../modules/OkModal';
import { GroupModal } from './GroupModal';
import { Team } from './Team';
import { TeamModal } from './TeamModal';

type Props = {
  groupSize: number;
  companyIndex: number;
  groupIndex: number;
  setValue: UseFormSetValue<{ companies: TreeCompany[] }>;
  getValues: UseFormGetValues<{ companies: TreeCompany[] }>;
  control: Control<{ companies: TreeCompany[] }>;
};
function Group({ groupSize, companyIndex, groupIndex, setValue, getValues, control }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const options = useDefaultOptions(language);

  const [display, setDisplay] = useState(false);
  const [isGroupModal, setIsGroupModal] = useState(false);
  const [isTeeamModal, setIsTeamModal] = useState(false);
  const [isCheckError, setIsCheckError] = useState(false);
  const ref = useRef(null);
  useClickAway(ref, () => {
    setDisplay(false);
  });

  const company = getValues().companies[companyIndex] || {};
  const group = (company.groups && company.groups[groupIndex]) || null;
  const { fields, append } = useFieldArray({
    name: `companies.${companyIndex}.groups.${groupIndex}.teams`,
    control,
  });

  const { mutateAsync, error } = usePostOrganizationsCheck<AxiosError<ErrorInfo>, unknown>(options);

  const handleHidden = async () => {
    await mutateAsync(
      {
        data: { organization_divisions: 2, hidden_id: group?.group_id },
      },
      {
        onSuccess: () => {
          setValue(`companies.${companyIndex}.groups.${groupIndex}.hidden`, group?.hidden === 0 ? 1 : 0);
        },
        onError: () => {
          setIsCheckError(true);
        },
      }
    );
  };

  const handleContextMenu = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e.preventDefault();
    setDisplay(true);
  };

  if (!group) return null;

  return (
    <div key={`group-${String(group.group_id)}`} className={`${groupSize === 1 ? 'bgnone' : ''}`}>
      <div className="sys-group2-in">
        <div id="group" ref={ref}>
          <p
            className={`name ${group.hidden === 1 ? 'grayoff' : ''}`}
            onContextMenu={(e) => handleContextMenu(e)}
            style={{ cursor: 'pointer' }}
          >
            {group.group_name_ja}
          </p>
          <ul className="pop" style={{ display: display ? 'block' : 'none' }}>
            <li className="modal-open" onClick={() => setIsGroupModal(true)}>
              <i className="fa-solid fa-pen-to-square" />
              {t('グループ編集')}
            </li>
            <li onClick={() => handleHidden()}>
              <i className="fa-solid fa-eye-slash" />
              {group?.hidden === 0 ? t('グループ非表示') : t('グループ表示')}
            </li>
            <li className="modal-open" onClick={() => setIsTeamModal(true)}>
              <i className="fa-solid fa-circle-plus" />
              {t('チーム追加')}
            </li>
          </ul>
          <div className={`group-line ${(group.teams?.length || 0) > 0 ? 'teams' : 'noteam'}`} />
        </div>
      </div>
      <div className="sys-group3">
        {fields?.map((team, i) => (
          <Team
            key={`team-${String(team.team_id)}`}
            teamSize={group.teams?.length || 0}
            companyIndex={companyIndex}
            groupIndex={groupIndex}
            teamIndex={i}
            getValues={getValues}
            setValue={setValue}
          />
        ))}
      </div>
      <GroupModal
        companyIndex={companyIndex}
        groupIndex={groupIndex}
        open={isGroupModal}
        onClose={() => setIsGroupModal(false)}
        getValues={getValues}
        setValue={setValue}
      />

      <TeamModal
        companyIndex={companyIndex}
        groupIndex={groupIndex}
        open={isTeeamModal}
        onClose={() => setIsTeamModal(false)}
        getValues={getValues}
        setValue={setValue}
        append={append}
      />

      {isCheckError && <OkModal onClickOk={() => setIsCheckError(false)}>{error?.response?.data?.message}</OkModal>}
    </div>
  );
}

export { Group };
